import React from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import Bugsnag from "@bugsnag/js";
import {
  Box,
  Typography,
  TextField,
  Snackbar,
  Button,
  Card,
  CardContent,
  Stack,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  Login as LoginIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import { BrowserView, MobileOnlyView } from "react-device-detect";
import Alert from "@mui/material/Alert";
import { Link, useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { styled } from "@mui/material/styles";

import UserData from "../../data/UserData";
import CoachData from "../../data/CoachData";
import { UserType } from "../../data/OrganizationData";

import organizationIdState from "../../atoms/organizationIdAtom";
import userDataState from "../../atoms/userDataAtom";

import logoIcon from "../../assets/Round-Logo-Design.png";
import logoImage from "../../assets/Logo.png";
import AppStore from "../../assets/ios/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg";
import useEvents from "../../hooks/useEvents";
import { ANALYTICS_EVENTS, WebAnalytics } from "@mighty45/analytics-shared";

import NewColors from "../../themes/NewColors";
import { getPasswordResetActionCodeSettings } from "../../../../../shared/auth/passwordResetActionCodeSettings";

// Styled Components following Mighty45 branding patterns
const SignInContainer = styled(Box)(({ theme }) => ({
  backgroundColor: NewColors.pageBackground, // Mighty45 brand background color
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(3),
}));

const SignInCard = styled(Card)(({ theme }) => ({
  maxWidth: 480,
  width: "100%",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
  backgroundColor: NewColors.white,
  overflow: "visible",
}));

const CardHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  paddingBottom: theme.spacing(2),
  textAlign: "center",
}));

const CardContentWrapper = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(0, 4, 4, 4),
  "&:last-child": {
    paddingBottom: theme.spacing(4),
  },
}));

const BrandTitle = styled(Typography)(({ theme }) => ({
  color: NewColors.status.error, // Mighty45 brand red
  fontWeight: "bold",
  marginBottom: theme.spacing(1),
  fontFamily: "Lato, sans-serif",
}));

const FormField = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(1),
    backgroundColor: NewColors.white,
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const SignInButton = styled(Button)(({ theme }) => ({
  backgroundColor: NewColors.status.error, // Mighty45 brand red
  color: NewColors.white,
  fontWeight: 600,
  fontSize: "1.1rem",
  padding: theme.spacing(1.5, 4),
  borderRadius: theme.spacing(1),
  textTransform: "none",
  boxShadow: `0 4px 12px rgba(220, 90, 71, 0.3)`,
  "&:hover": {
    backgroundColor: NewColors.triadicPower.buttons.primaryHover,
    boxShadow: `0 6px 16px rgba(220, 90, 71, 0.4)`,
  },
  "&:disabled": {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[500],
  },
}));

const ForgotPasswordButton = styled(Button)(({ theme }) => ({
  color: theme.palette.primary.main,
  textTransform: "none",
  fontWeight: 500,
  padding: theme.spacing(0.5, 1),
  "&:hover": {
    backgroundColor: "transparent",
    textDecoration: "underline",
  },
}));

const OnboardingSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: NewColors.sectionBackground,
  borderRadius: theme.spacing(1),
  textAlign: "center",
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

// import googleButtonImage from "../../assets/google/btn_google_signin_dark_normal_web@2x.png";

// const googleProvider = new firebase.auth.GoogleAuthProvider();

const SignInPage = () => {
  const navigate = useNavigate();
  const setOrganizationIdState = useSetRecoilState(organizationIdState);
  const setUserDataState = useSetRecoilState(userDataState);

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [errorOpen, setErrorOpen] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const events = useEvents();

  // Create analytics instance
  const analytics = React.useMemo(() => new WebAnalytics({}), []);

  const [showPassword, setShowPassword] = React.useState(false);

  React.useEffect(() => {
    const email = localStorage.getItem("signInEmail");
    if (email) {
      setEmail(email);
    }

    // Track sign in page view (only once on mount)
    analytics.trackScreenView("SignIn", {
      entry_point: "direct",
      has_saved_email: !!email,
    });
  }, []); // Empty dependency array - only run on mount

  function showError(message) {
    setErrorMessage(message);
    setErrorOpen(true);
  }

  function clearError() {
    setErrorMessage("");
    setErrorOpen(false);
  }

  async function handleSignInCheck(credentials) {
    await firebase.auth().currentUser.getIdTokenResult(true);

    localStorage.setItem("signInEmail", email);

    const userData = await UserData.getUserDataByAuth(credentials.user.uid);
    if (userData && userData.uid) {
      if (userData.type === UserType.client) {
        // No clients allowed.
        showError("This account cannot access the coach console.");
        navigate("/signin");
        return;
      }

      // If they aren't in an org yet, then go to the onboarding screen.
      if (!userData.organizationId) {
        navigate("/onboarding");
        return;
      }

      // If they are, then test the coach status.
      const coach = await CoachData.getCoach(userData.uid);
      if (coach && coach.status === "disabled") {
        showError("This account is disabled. Contact the administrator.");
        navigate("/signin");
        return;
      }

      // set the uid to the users uid, not the auth uid.
      localStorage.setItem("uid", userData.uid);
      localStorage.setItem("credentials.user.uid", credentials.user.uid);
      localStorage.setItem("email", credentials.user.email);
      localStorage.setItem("organizationId", userData.organizationId);

      // Set analytics user properties
      analytics.setUserId(userData.uid);
      analytics.setUserProperties({
        user_type: userData.type || "coach",
        organization_id: userData.organizationId,
        email: credentials.user.email,
        is_active: true,
        sign_in_method: "email_password",
      });

      // Track successful sign-in completion
      analytics.trackCustomEvent(ANALYTICS_EVENTS.USER_LOGIN, {
        success: true,
        user_type: userData.type || "coach",
        organization_id: userData.organizationId,
        has_organization: true,
        destination: "home",
      });

      setUserDataState(userData);
      setOrganizationIdState(userData.organizationId);

      navigate("/home");
    } else {
      showError(
        `There is a problem signing in with that email address. No account exists with that email address`,
      );
    }
  }

  async function handleSignIn() {
    const signInStartTime = Date.now();

    // Track sign-in attempt
    analytics.trackCustomEvent(ANALYTICS_EVENTS.USER_LOGIN, {
      method: "email_password",
      has_saved_email: !!localStorage.getItem("signInEmail"),
    });

    try {
      await firebase
        .auth()
        .setPersistence(firebase.auth.Auth.Persistence.LOCAL);
      const credentials = await firebase
        .auth()
        .signInWithEmailAndPassword(email, password);

      // Track successful authentication
      const signInDuration = Date.now() - signInStartTime;
      analytics.trackPerformance("sign_in_duration", signInDuration, {
        success: true,
        method: "email_password",
      });

      await handleSignInCheck(credentials);
    } catch (e) {
      Bugsnag.notify(e);

      // Track sign-in error
      analytics.trackError(e, {
        screen_name: "SignIn",
        user_action: "sign_in_attempt",
        email_provided: !!email,
        error_context: "firebase_auth",
      });

      showError(`There is a problem signing in. Try again. (${e.message})`);
    }
  }

  function validate() {
    if (email.length && password.length > 0) {
      return true;
    }
    return false;
  }

  const handleOnClick = React.useCallback(() => {
    if (email && email.length > 0) {
      events.logButtonClick({
        buttonName: "Password Reset",
        pageName: "Signin",
      });

      // Track password reset request
      analytics.trackCustomEvent("user_action", {
        action: "password_reset_requested",
        element: "forgot_password_button",
        has_email: true,
      });

      firebase
        .auth()
        .sendPasswordResetEmail(email, getPasswordResetActionCodeSettings())
        .then(() => {
          analytics.trackCustomEvent("password_reset_email_sent", {
            success: true,
            email_domain: email.split("@")[1] || "unknown",
          });
          showError("A password reset email was sent to your email address.");
        })
        .catch((e) => {
          Bugsnag.notify(e);
          analytics.trackError(e, {
            screen_name: "SignIn",
            user_action: "password_reset",
            error_context: "firebase_password_reset",
          });
          showError("There was a problem sending the password reset email.");
        });
    }
  }, [email, analytics]);

  return (
    <>
      <BrowserView>
        <SignInContainer
          onKeyDown={async (e) => {
            if (e.key === "Enter") {
              if (validate()) {
                await handleSignIn();
              } else {
                showError("A password is required.");
              }
            }
          }}
        >
          <SignInCard>
            <CardHeader>
              <a href="https://mighty45.com">
                <img
                  alt="Mighty45 Logo"
                  width="146px"
                  height="78px"
                  src={logoImage}
                  style={{ marginBottom: "16px" }}
                />
              </a>
              <BrandTitle variant="h3">Sign In</BrandTitle>
              <Typography variant="body1" color="text.secondary">
                Access your Mighty45 Coach Console
              </Typography>
            </CardHeader>

            <CardContentWrapper>
              <FormField>
                <IconWrapper>
                  <EmailIcon sx={{ mr: 1, fontSize: 20 }} />
                  <Typography variant="h6" fontWeight="600">
                    Email Address
                  </Typography>
                </IconWrapper>
                <StyledTextField
                  fullWidth
                  variant="outlined"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    if (event.target.value && !email) {
                      // Track first email input
                      analytics.trackCustomEvent("user_action", {
                        action: "email_entered",
                        element: "email_field",
                        had_saved_email: !!localStorage.getItem("signInEmail"),
                      });
                    }
                  }}
                  inputProps={{
                    autoCapitalize: "none",
                    type: "email",
                  }}
                />
              </FormField>

              <FormField>
                <IconWrapper>
                  <LockIcon sx={{ mr: 1, fontSize: 20 }} />
                  <Typography variant="h6" fontWeight="600">
                    Password
                  </Typography>
                </IconWrapper>
                <StyledTextField
                  fullWidth
                  variant="outlined"
                  placeholder="Enter your password"
                  type={showPassword ? "text" : "password"}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    if (event.target.value && !password) {
                      // Track first password input
                      analytics.trackCustomEvent("user_action", {
                        action: "password_entered",
                        element: "password_field",
                        has_email: !!email,
                      });
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => {
                            analytics.trackCustomEvent("user_action", {
                              action: "password_visibility_toggled",
                              element: "visibility_button",
                              new_state: showPassword ? "hidden" : "visible",
                            });
                            setShowPassword(!showPassword);
                          }}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormField>

              <Box sx={{ textAlign: "center", mb: 3 }}>
                <ForgotPasswordButton onClick={handleOnClick}>
                  Forgot your password?
                </ForgotPasswordButton>
              </Box>

              <Box sx={{ textAlign: "center", mb: 3 }}>
                <SignInButton
                  fullWidth
                  size="large"
                  startIcon={<LoginIcon />}
                  onClick={async () => {
                    // Track sign-in button click
                    analytics.trackCustomEvent("user_action", {
                      action: "sign_in_clicked",
                      element: "sign_in_button",
                      form_valid: validate(),
                      has_email: !!email,
                      has_password: !!password,
                    });

                    if (validate()) {
                      await handleSignIn();
                    } else {
                      analytics.trackCustomEvent(
                        ANALYTICS_EVENTS.ERROR_OCCURRED,
                        {
                          error_type: "form_validation",
                          error_message: "Password required for sign-in",
                          screen_name: "SignIn",
                        },
                      );
                      showError("A password is required.");
                    }
                  }}
                  disabled={!validate()}
                >
                  Sign In to Console
                </SignInButton>
              </Box>

              <OnboardingSection>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <BusinessIcon color="primary" sx={{ fontSize: 20 }} />
                  <Typography variant="subtitle1" fontWeight="600">
                    Start Your Organization
                  </Typography>
                </Stack>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Ready to launch your fitness business?
                </Typography>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/onboarding"
                  onClick={() => {
                    events.logHyperlink({
                      objectName: "New Organization",
                      hyperlink: "/onboarding",
                    });
                  }}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 1,
                  }}
                >
                  Get Started Here
                </Button>
              </OnboardingSection>
            </CardContentWrapper>
          </SignInCard>
        </SignInContainer>

        <Snackbar
          open={errorOpen}
          autoHideDuration={6000}
          onClose={() => clearError()}
        >
          <Alert
            onClose={() => clearError()}
            severity="error"
            sx={{ borderRadius: 1 }}
          >
            {errorMessage}
          </Alert>
        </Snackbar>
      </BrowserView>
      <MobileOnlyView>
        <Box
          sx={{
            backgroundColor: NewColors.pageBackground,
            minHeight: "100vh",
            p: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Card
            sx={{
              maxWidth: 400,
              width: "100%",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              backgroundColor: NewColors.white,
            }}
          >
            <CardContent sx={{ p: 4, textAlign: "center" }}>
              <Box sx={{ mb: 3 }}>
                <img width="80%" src={logoIcon} alt="Mighty45 Logo" />
              </Box>

              <BrandTitle variant="h4" sx={{ mb: 1 }}>
                Mighty45 Coach
              </BrandTitle>

              <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                Install the mobile app to manage your clients on the go
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                The full coaching experience is optimized for mobile devices
                with our dedicated app.
              </Typography>

              <Box sx={{ mb: 3 }}>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://apps.apple.com/us/app/mighty45/id1594939720"
                >
                  <img
                    width="200"
                    src={AppStore}
                    alt="Download on App Store"
                    style={{ borderRadius: "8px" }}
                  />
                </a>
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: "italic" }}
              >
                For desktop access, please use a larger screen
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </MobileOnlyView>
    </>
  );
};

export default SignInPage;
